export const en = {
  app: {
    name: 'Mahjong',
    title: 'Mahjong',
  },
  seo: {
    title:
      'Learn Mahjong Online — 4-Player Chinese Mahjong with a Coach | BN-games',
    description:
      'Learn to play real 4-player mahjong: guided lessons, a coach that suggests discards and explains why, Chinese Classical rules, and online tables where bots fill empty seats. Free.',
    social:
      'Learn 4-player mahjong with lessons and a coach, then play Chinese Classical online with friends and bots.',
    h1:
      'Learn and play mahjong online',
    intro:
      'This is mahjong as a four-player game of skill — not mahjong solitaire. Each player builds a hand of fourteen tiles into four sets and a pair, drawing from the wall and claiming other players’ discards.\n\nBN-games teaches it step by step: a trainer with six guided lessons, a coach that suggests a discard and explains why, and a glossary of tiles, sets and calls. When you are ready, play Chinese Classical mahjong — with kongs, traditional scoring and East/South matches — or open an online table for friends, with bots filling any empty seat.',
    rulesTitle:
      'Mahjong Rules for Beginners — Tiles, Sets & Calls | BN-games',
    rulesDescription:
      'Mahjong rules for beginners: the tile families, building four sets and a pair, the pung, chi and kong calls, and the flow of a turn.',
    rulesH1:
      'Mahjong rules for beginners',
    playCta:
      'Start learning mahjong',
  },
  common: {
    back: '← Back',
  },
  auth: {
    signIn: 'Sign in',
    signOut: 'Sign out',
    google: 'Google',
    github: 'GitHub',
    completeProfile: 'Complete your profile →',
    profile: 'Profile',
    backToLanding: '← BN-games',
  },
  social: {
    close: 'Close',
    invite: {
      message: '{{sender}} invites you to {{game}}',
      moreCount_one: '+{{count}} more invite',
      moreCount_few: '+{{count}} more invites',
      moreCount_many: '+{{count}} more invites',
      moreCount_other: '+{{count}} more invites',
      accept: 'Accept',
      decline: 'Decline',
      acceptAria: "Accept {{sender}}'s {{game}} invite",
      declineAria: "Decline {{sender}}'s {{game}} invite",
    },
    dock: {
      pillLabel: 'From {{sender}}',
      pillAria: 'Invite from {{sender}} to {{game}} — show',
      popoverLabel: 'Invite',
      close: 'Close invite',
      leaveGameTitle: 'Leave this game?',
      leaveGameBody: 'Accepting opens {{game}} and leaves the current game. In an online game your seat is held for a while, so you can come back.',
      leaveGameConfirm: 'Leave and join',
      leaveGameCancel: 'Stay',
    },
    friendPicker: {
      button: 'Invite friend',
      modalTitle: 'Invite a friend',
      loading: 'Loading friends…',
      signInPrompt: 'Sign in to invite a friend.',
      empty: "You don't have any friends yet.",
      findFriends: 'Find friends',
      invite: 'Invite',
      inviteAria: 'Invite {{name}}',
      invited: 'Invited',
      invitedAria: 'Invited {{name}}',
      notAccepting: "This player isn't accepting invites right now",
      retryIn: 'Try again in {{seconds}}s',
    },
    addOpponent: {
      buttonMany: 'Add friends…',
      modalTitle: 'Add friends',
      add: 'Add {{name}} as friend',
      added: 'Added',
      addedAria: 'Added {{name}} as friend',
    },
  },
  menu: {
    play: 'Play',
    freePlay: 'Free Play',
    lessons: 'Lessons',
    playOnline: 'Play online',
    joinOnline: 'Join a table',
    comingSoon: 'Coming soon',
    rules: 'Rules',
    settings: 'Settings',
    about: 'About',
    resume: 'Resume',
    resumeOnline: 'Resume online ({{code}})',
  },
  pause: {
    title: 'Paused',
    openTooltip: 'Pause menu',
    resume: 'Resume',
    settings: 'Settings',
    quit: 'Quit to menu',
  },
  about: {
    title: 'About',
    body: 'A trainer for learning simplified mahjong — one human player against three bots. Read the glossary to learn the tiles, sets, and calls, then play a full hand.',
  },
  game: {
    yourTurn: 'Your turn',
    seatTurn: "{{seat}}'s turn",
    seatEast: 'East',
    seatSouth: 'South',
    seatWest: 'West',
    seatNorth: 'North',
    discard: 'Discard',
    draw: 'Draw',
    wallCount: 'Tiles left: {{count}}',
    tsumo: 'Tsumo',
    ron: 'Ron',
    exhausted: 'Drawn hand',
    winner: '{{seat}} wins',
    nextHand: 'Next hand',
  },
  claim: {
    pung: 'Pung',
    chi: 'Chi',
    ron: 'Ron',
    kong: 'Kong',
    tsumo: 'Tsumo',
    skip: 'Skip',
  },
  tile: {
    suitMan: 'Characters',
    suitPin: 'Circles',
    suitSou: 'Bamboo',
    windEast: 'East Wind',
    windSouth: 'South Wind',
    windWest: 'West Wind',
    windNorth: 'North Wind',
    dragonRed: 'Red Dragon',
    dragonGreen: 'Green Dragon',
    dragonWhite: 'White Dragon',
  },
  coach: {
    title: 'Coach',
    hide: 'Hide coach',
    setsCount: '{{n}} complete sets',
    pairOne: '1 pair',
    pairNone: 'no pair yet',
    floatersCount: '{{n}} floaters',
    hint: 'Hint',
    tileName: '{{rank}} {{suit}}',
    discardIntro: 'Consider discarding {{tile}}',
    claimTitle: 'Claim available',
    reason: {
      loneHonor: "This honour can't form a run — it's spare weight.",
      terminal: 'A terminal ({{tile}}) connects fewer ways than a middle tile.',
      noConnection: '{{tile}} has no neighbours to build a run or pair on.',
      excessCopy: 'You already have three of these — the extra {{tile}} is spare.',
    },
    claim: {
      ron: 'Ron — this discard completes your hand. You can win!',
      pung: 'Pung — this would complete a triplet.',
      chi: 'Chi — this would complete a run.',
    },
  },
  lesson: {
    listTitle: 'Lessons',
    empty: 'Lessons are coming soon.',
    progress: 'Step {{n}} of {{total}}',
    next: 'Next',
    skip: 'Skip lesson',
    exit: 'Exit lesson',
    tryAgain: 'Try again',
    complete: 'Lesson complete!',
    wrongTile: 'Not that tile — follow the highlight and try again.',
    wrongAction: "That's not the move this step is teaching. Try again.",
    notYourTurn: "It's not your turn to do that yet.",
    illegalMove: "You can't play that tile right now.",
    cannotClaim: "You can't make that call here.",
    cannotTsumo: 'Your hand is not complete yet.',
    shouldClaim: 'This discard is worth claiming — go ahead.',
    script: {
      tiles: {
        title: 'Tiles & Families',
        intro: 'Welcome! Mahjong uses 136 tiles. Let\'s meet the families in your hand before you play a move.',
        suits: 'Three numbered suits run 1 to 9: Characters, Circles, and Bamboo. The highlighted tiles show one of each.',
        winds: 'The four winds — East, South, West, North — are honour tiles with no number. Two are highlighted here.',
        dragons: 'The three dragons — Red, Green, White — are the other honours. Your Red Dragon is highlighted.',
        tryDiscard: 'A turn ends by discarding one tile. Tap the highlighted 7-Bamboo to send it to the pond.',
        done: 'That is every family. Remember: honours never form runs — keep that in mind as you learn the sets.',
      },
      firstRun: {
        title: 'Your First Run',
        intro: 'A run is three tiles in a row of one suit. You hold 3 and 4 of Characters — a 2 or a 5 completes it.',
        discard: 'The White Dragon sits alone and can never join a run. Discard it to keep your run alive.',
        done: 'Well done. Holding connected tiles and shedding loners is the heart of building a hand.',
      },
      triplets: {
        title: 'Triplets & the Pair',
        intro: 'Two matching tiles can grow into a triplet, and every winning hand needs exactly one pair. You hold two 5-Bamboo and two 3-Circle.',
        discard: 'The lone West wind connects to nothing. Discard it and keep your pairs.',
        done: 'One pair becomes your pair; another might grow into a triplet. Matching tiles are worth holding.',
      },
      pung: {
        title: 'Calling Pung',
        intro: 'You hold a pair of Red Dragons. If any player discards the third, you may call Pung to make an open triplet.',
        discard: 'First discard a tile you do not need — the lone White Dragon. Then watch the other players.',
        claim: 'A Red Dragon was discarded! Tap Pung to claim it and complete your triplet.',
        done: 'You made an open Pung. After any call you discard without drawing — the turn is now yours.',
      },
      chi: {
        title: 'Calling Chi',
        intro: 'Chi completes a run, but only from the player to your left. You hold 2 and 3 of Bamboo, waiting on a 1.',
        discard: 'Discard your lone White Dragon while you wait for the tile you need.',
        reading: 'Watch what opponents discard — it hints at what they do not need. The player on your left just threw the 1-Bamboo that completes your run.',
        claim: 'Tap Chi to take the 1-Bamboo and complete your run of Bamboo.',
        done: 'That is a called run. Remember: Pung is from anyone, but Chi only from the player on your left.',
      },
      winning: {
        title: 'Winning — Tsumo & Ron',
        intro: 'Your hand is one tile from complete: three runs, a pair of Red Dragons, and 5-6 Bamboo waiting on a 4 or 7.',
        discard: 'Discard the lone White Dragon — every other tile is already doing work.',
        ways: 'There are two ways to win: draw the winning tile yourself (Tsumo), or claim it from another player\'s discard (Ron).',
        tsumo: 'You drew the 7-Bamboo — your hand is complete! Declare Tsumo to win.',
        done: 'You won by Tsumo. Had an opponent discarded that 7-Bamboo, you would have called Ron instead. Congratulations!',
      },
    },
  },
  match: {
    round: '{{round}} Round',
    dealer: 'Dealer: {{seat}}',
    you: 'You',
    score: '{{seat}}: {{points}}',
  },
  score: {
    points: 'Points: {{n}}',
    patterns: {
      wonHand: 'Winning Hand',
      selfDrawn: 'Self-Drawn',
      meldPoints: 'Meld Points',
      pairBonus: 'Pair Bonus',
      sevenPairs: 'Seven Pairs',
      allTriplets: 'All Triplets',
      allSimples: 'All Simples',
      halfFlush: 'Half Flush',
      fullFlush: 'Full Flush',
      allHonors: 'All Honors',
      mixedTerminals: 'Mixed Terminals',
      pureTerminals: 'Pure Terminals',
      greatDragons: 'Great Dragons',
      greatWinds: 'Great Winds',
      thirteenOrphans: 'Thirteen Orphans',
      heavenlyHand: 'Heavenly Hand',
      earthlyHand: 'Earthly Hand',
    },
  },
  online: {
    overlay: {
      playerDisconnected: 'A player disconnected',
      waitingForReconnection: 'The table is paused — {{countdown}} left to reconnect.',
      reconnecting: 'Reconnecting…',
      restoringGame: 'Restoring your seat — {{countdown}} left.',
      connectionLost: 'Connection lost',
      gameEnded: 'This table can no longer be reached.',
      rematchOffered: 'Rematch offered',
      waitingForOthers: 'Waiting for the other players…',
      rematchRequested: 'A player wants a rematch',
      rematchPrompt: 'A rematch deals a brand-new match.',
      rematchDeclined: 'Rematch declined',
      gameOverWin: 'You win',
      gameOverLoss: 'You lose',
      gameOverDraw: 'A tie',
      gameOverPrompt: 'Play another?',
      buttons: {
        leave: 'Leave',
        rematch: 'Rematch',
        returnToMenu: 'Back to menu',
        cancel: 'Cancel',
        accept: 'Accept',
        decline: 'Decline',
      },
    },
    rematchConfirm: {
      title: 'Start a new match?',
      message:
        'This match is still in progress. A rematch deals a brand-new one — the running scores, the round, and the dealer are all discarded.',
      confirm: 'New match',
      cancel: 'Keep playing',
    },
    create: {
      title: 'Create a table',
      rulesetLabel: 'Ruleset',
      humansLabel: 'Players',
      botsHint: 'Every seat nobody takes is played by a bot.',
      verifiableLabel: 'Verifiable shuffle',
      verifiableHint: 'The wall is shuffled from a public random beacon, so anyone can check the deal was not rigged.',
      on: 'On',
      off: 'Off',
      submit: 'Create table',
      connecting: 'Connecting…',
      error: 'Could not reach the server. Try again.',
    },
    join: {
      title: 'Join a table',
      codeLabel: 'Room code',
      codeHint: 'Four characters, from the player who created the table.',
      submit: 'Join',
      connecting: 'Joining…',
      error: 'Could not join that table. Try again.',
      errorNotFound: 'No table with that code. Check it and try again.',
      errorFull: 'That table is already full.',
    },
    lobby: {
      title: 'Table',
      codeLabel: 'Room code',
      copyCode: 'Copy',
      copied: 'Copied',
      share: 'Share this code with the players joining you.',
      rulesetLabel: 'Ruleset: {{ruleset}}',
      verifiableOn: 'Verifiable shuffle is on.',
      seat: 'Seat {{n}}',
      you: 'You',
      bot: 'Bot',
      open: 'Waiting…',
      startNow: 'Start now',
      startNowHint: 'Starts immediately — every empty seat becomes a bot.',
      leave: 'Leave table',
      status: {
        connecting: 'Connecting…',
        waiting: 'Waiting for players…',
        ready: 'Everyone is here — starting.',
        error: 'Lost contact with the table.',
      },
    },
    resumeModal: {
      reconnectingTitle: 'Reconnecting…',
      reconnectingBody: 'Reconnecting to your table…',
      seatTakenTitle: 'Seat taken',
      seatTakenBody: 'That seat belongs to another account. Sign in with that account, or start a new table.',
      roomGoneTitle: 'Table closed',
      roomGoneBody: 'This table is gone — it closed or was never opened here.',
      roomFullTitle: 'Table full',
      roomFullBody: 'Every seat at this table is taken, and none of them is yours.',
      anonymousTitle: "We can't confirm this seat is yours",
      anonymousBody: 'This browser has no record of the table. Sign in before your next online game and your seat follows you to any device.',
      unreachableTitle: "Couldn't reach the server",
      unreachableBody: "The server didn't answer. Check your connection and try again.",
      signIn: 'Sign in',
      retry: 'Try again',
    },
  },
  verify: {
    openButton: 'Verify the deal',
    title: 'Verify the deal',
    explainer:
      'The wall for this hand was shuffled from a public random beacon. Now that the hand is over its seed can be published, so this check re-derives the wall from the beacon round and confirms nobody chose it.',
    source: 'Source',
    chain: 'Chain',
    signedBy: 'Signed by',
    round: 'Round',
    randomness: 'Randomness',
    room: 'Room code',
    seed: 'Wall seed',
    derivedSeed: 'Derived seed',
    statusPending: 'Checking…',
    statusVerified: 'Verified — this wall came from the beacon.',
    statusSeedMismatch: 'The wall does not match this beacon round.',
    statusSigInvalid: 'The proof is not valid.',
    statusUnverified: 'This hand has no proof.',
    close: 'Close',
  },
  settings: {
    title: 'Settings',
    language: 'Language',
    theme: 'Theme',
    coach: 'Coach hints',
    turnStartSound: 'Your-turn sound',
    variant: 'Ruleset',
    languages: {
      en: 'English',
      ru: 'Русский',
    },
    variants: {
      simplified: 'Simplified',
      classical: 'Classical',
    },
  },
  rules: {
    title: 'Rules & Glossary',
    tileFamilies: 'Tile Families',
    tileFamiliesBody:
      'Mahjong tiles come in three numbered suits plus two kinds of honour tiles.\n\nCharacters (萬, "man") run 1 to 9. Circles (筒, "pin") run 1 to 9, drawn as rings of dots. Bamboo (索, "sou") run 1 to 9, drawn as green sticks.\n\nHonours have no rank. The four winds are East, South, West, and North. The three dragons are Red, Green, and White. Each of the 34 distinct tiles appears four times, for 136 tiles in all.',
    sets: 'Sets',
    setsBody:
      'A winning hand is fourteen tiles arranged as four sets plus one pair.\n\nA run is three consecutive tiles of the same suit, such as 3-4-5 of circles — honours never form runs. A triplet is three identical tiles, such as three East winds. A pair is two identical tiles, and every winning hand needs exactly one.',
    calls: 'Calls',
    callsBody:
      'When another player discards a tile you need, you may claim it out of turn.\n\nPung — claim any player\'s discard to complete a triplet. Chi — claim a discard to complete a run, but only from the player to your left (the seat just before your turn). Ron — claim a discard that completes your whole hand and win. Tsumo — win on a tile you drew yourself. After a Pung or Chi you take the tile, expose the set, and discard without drawing.',
    turnFlow: 'Turn Flow',
    turnFlowBody:
      'On your turn you draw one tile from the wall. If it completes your hand you may declare Tsumo and win; otherwise you discard one tile face-up.\n\nAfter a discard, any other player may claim it. When several players want the same tile, Ron beats Pung, and Pung beats Chi. If nobody claims it, the next player draws. If the wall runs out before anyone wins, the hand ends with no winner.',
  },
};
